import React from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent, EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

const bgImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultValue: '' },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page hero image' },
  },
};

export function HyperlocalHero() {
  const bgImage = useSlotGroup('hyperlocalhero.bg_image', bgImageDef);

  return (
    <section className="relative w-full h-[70vh] min-h-[450px] max-h-[700px] overflow-hidden">
      <EditableSlotGroup groupPath="hyperlocalhero.bg_image" definition={bgImageDef}>
        <div className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              className="w-full h-full object-cover"
              style={{
                animation: 'hlKenBurns 30s ease-in-out infinite alternate',
                willChange: 'transform',
                transformOrigin: 'bottom right',
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20" />

      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8">
        <RichTextContent
          path="hyperlocalhero.title"
          label="Title"
          defaultValue="<p>Hyperlocal Page Title</p>"
          as="h1"
          className="hl-entrance hl-entrance-1 font-heading text-4xl sm:text-5xl md:text-6xl font-normal mb-5 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg"
        />
        <RichTextContent
          path="hyperlocalhero.subtitle"
          label="Subtitle"
          defaultValue="<p>Explore apartments in your area</p>"
          as="h2"
          className="hl-entrance hl-entrance-2 font-body text-lg sm:text-xl md:text-2xl max-w-3xl leading-relaxed opacity-90 drop-shadow-md font-normal"
        />
      </div>

      <style>{`
        @keyframes hlKenBurns {
          0% { transform: scale(1.01) translate(0, 0); }
          100% { transform: scale(1.05) translate(-0.8%, -0.5%); }
        }
        @keyframes hlEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hl-entrance {
          animation: hlEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hl-entrance-1 { animation-delay: 0.2s; }
        .hl-entrance-2 { animation-delay: 0.45s; }
        @media (prefers-reduced-motion: reduce) {
          .hl-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HyperlocalHero',
  displayName: 'Hyperlocal Hero',
  tokens: {
    'hyperlocalhero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Hyperlocal Page Title</p>',
    },
    'hyperlocalhero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Explore apartments in your area</p>',
    },
  },
  slotGroups: {
    'hyperlocalhero.bg_image': {
      label: 'Background Image',
      fields: {
        image: { label: 'Image', type: 'image', defaultValue: '' },
        alt: { label: 'Alt Text', type: 'text', defaultValue: 'Page hero image' },
      },
    },
  },
};

(HyperlocalHero as any).contentSchema = contentSchema;

export default HyperlocalHero;
